import type { Notification as AppNotification } from '@recipes/contracts';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Label, Skeleton, Switch, cn } from '@recipes/ui';
import { formatDistanceToNow } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import {
  currentPushSubscription,
  pushSupport,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/features/notifications/push-toggle';
import { api } from '@/lib/api';
import { describeWorkerError, toastError } from '@/lib/errors';
import { usePublicConfig } from '@/lib/hooks/use-public-config';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

/** Where a notification leads when clicked, derived from its type and data payload. */
export function notificationLink(n: AppNotification): string | null {
  const data = n.data ?? {};
  const recipeId = typeof data.recipeId === 'string' ? data.recipeId : null;
  switch (n.type) {
    case 'IMPORT_READY':
    case 'IMPORT_FAILED':
      return typeof data.jobId === 'string' ? `/add/import/${data.jobId}` : '/add/import';
    case 'AI_READY':
    case 'AI_FAILED':
      return typeof data.generationId === 'string' ? `/add/ai/${data.generationId}` : '/add';
    case 'TIMER_DONE':
      return recipeId ? `/cook/${recipeId}` : '/cook';
    case 'HOUSEHOLD_INVITE':
    case 'HOUSEHOLD_CHANGE':
      return typeof data.householdId === 'string'
        ? `/households/${data.householdId}`
        : '/households';
    case 'EXPORT_READY':
      return '/profile/data';
    case 'SHOPPING_REMINDER':
      return typeof data.listId === 'string' ? `/shopping/${data.listId}` : '/shopping';
    case 'SUPPORT_MESSAGE':
      return typeof data.conversationId === 'string'
        ? `/admin/support/${data.conversationId}`
        : '/admin/support';
    case 'SYSTEM':
      // Only addresses inside the app – a notification never sends anyone elsewhere.
      return typeof data.url === 'string' && data.url.startsWith('/') && !data.url.startsWith('//')
        ? data.url
        : recipeId
          ? `/recipes/${recipeId}`
          : null;
    default:
      return recipeId ? `/recipes/${recipeId}` : null;
  }
}

/**
 * A notification in the reader's **current** language: rendered from its key and values when
 * this build knows the key, else the text the server wrote when it was created.
 */
function useNotificationText() {
  const { t, i18n } = useTranslation();
  return (n: AppNotification): { title: string; body: string | null } => {
    const key = n.messageKey ? `notifications.messages.${n.messageKey}` : null;
    if (!key || !i18n.exists(`${key}.title`)) return { title: n.title, body: n.body };
    const params: Record<string, string | number> = { ...(n.params ?? {}) };
    // Failure reasons travel as codes and become words here, in the current language.
    if (typeof params.reasonCode === 'string')
      params.reason = describeWorkerError(params.reasonCode);
    if (typeof params.importCode === 'string') {
      const importKey = `imports.errors.${params.importCode}`;
      params.reason = i18n.exists(importKey) ? t(importKey) : t('errors.IMPORT_FAILED');
    }
    const body = i18n.exists(`${key}.body`) ? t(`${key}.body`, params).trim() : '';
    return { title: t(`${key}.title`, params), body: body || null };
  };
}

function PushToggle() {
  const { t } = useTranslation();
  const config = usePublicConfig();
  const key = config.data?.push.vapidPublicKey ?? null;
  const support = pushSupport(key);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (support !== 'ready') return;
    void currentPushSubscription().then((s) => setEnabled(s !== null));
  }, [support]);

  const toggle = async (next: boolean) => {
    if (!key) return;
    setBusy(true);
    try {
      if (next) await subscribeToPush(key);
      else await unsubscribeFromPush();
      setEnabled(next);
      toast.success(next ? t('notifications.push.enabled') : t('notifications.push.disabled'));
    } catch (error) {
      const message =
        error instanceof Error && error.message === 'PERMISSION_DENIED'
          ? t('notifications.push.denied')
          : error instanceof Error && error.message === 'NO_SERVICE_WORKER'
            ? t('notifications.push.noServiceWorker')
            : null;
      if (message) toast.error(t('notifications.push.failed'), message);
      else toastError(error, t('notifications.push.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border p-3" data-testid="push-toggle">
      {support === 'ready' ? (
        <Bell className="size-5 text-primary" aria-hidden />
      ) : (
        <BellOff className="size-5 text-muted-foreground" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <Label htmlFor="push-switch" className="block">
          {t('notifications.push.title')}
        </Label>
        <p className="text-sm text-muted-foreground">
          {support === 'unsupported'
            ? t('notifications.push.unsupported')
            : support === 'no-key'
              ? t('notifications.push.notConfigured')
              : support === 'denied'
                ? t('notifications.push.denied')
                : t('notifications.push.description')}
        </p>
      </div>
      <Switch
        id="push-switch"
        checked={enabled}
        disabled={support !== 'ready' || busy}
        onCheckedChange={(v) => void toggle(v)}
      />
    </div>
  );
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const text = useNotificationText();
  const queryClient = useQueryClient();
  const dateLocale = currentLocale() === 'de' ? deLocale : enUS;
  const query = useInfiniteQuery({
    queryKey: queryKeys.notifications,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.notifications.list({ cursor: pageParam, limit: 30 }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
  const markRead = useMutation({
    mutationFn: (id: string) => api.notifications.markRead(id),
    onSuccess: invalidate,
  });
  const markAll = useMutation({
    mutationFn: () => api.notifications.markAllRead(),
    onSuccess: invalidate,
    onError: (e) => toastError(e),
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = query.data?.pages[0]?.unreadCount ?? 0;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 md:p-6" data-testid="notifications-page">
      <PageHeader
        title={t('notifications.title')}
        description={unread > 0 ? t('notifications.unread', { count: unread }) : undefined}
        actions={
          <Button
            variant="outline"
            onClick={() => markAll.mutate()}
            disabled={unread === 0}
            loading={markAll.isPending}
            data-testid="mark-all-read"
          >
            <CheckCheck aria-hidden /> {t('notifications.markAllRead')}
          </Button>
        }
      />
      <PushToggle />
      {query.isPending ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          title={t('app.unexpectedError')}
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          illustration={<Illustration name="notifications" />}
          title={t('notifications.empty')}
          description={t('notifications.emptyHint')}
        />
      ) : (
        <>
          <ul className="divide-y rounded-lg border" data-testid="notification-list">
            {items.map((n) => {
              const link = notificationLink(n);
              const isUnread = n.readAt === null;
              return (
                <li
                  key={n.id}
                  className={cn('flex gap-3 p-3', isUnread && 'bg-accent/30')}
                  data-testid="notification-item"
                  data-unread={isUnread}
                >
                  <span
                    className={cn(
                      'mt-2 size-2 shrink-0 rounded-full',
                      isUnread ? 'bg-primary' : 'bg-transparent',
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {link ? (
                        <Link
                          to={link}
                          className="font-medium underline-offset-4 hover:underline"
                          onClick={() => isUnread && markRead.mutate(n.id)}
                        >
                          {text(n).title}
                        </Link>
                      ) : (
                        <span className="font-medium">{text(n).title}</span>
                      )}
                      {isUnread && <Badge variant="default">{t('notifications.new')}</Badge>}
                      <Badge variant="outline">{t(`notifications.type.${n.type}`)}</Badge>
                    </div>
                    {text(n).body && (
                      <p className="text-sm text-muted-foreground">{text(n).body}</p>
                    )}
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      <time dateTime={n.createdAt}>
                        {formatDistanceToNow(new Date(n.createdAt), {
                          addSuffix: true,
                          locale: dateLocale,
                        })}
                      </time>
                    </p>
                  </div>
                  {isUnread && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => markRead.mutate(n.id)}
                      aria-label={`${t('notifications.markRead')}: ${text(n).title}`}
                      data-testid="mark-read"
                    >
                      {t('notifications.markRead')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {query.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => void query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
